// Relative path: src/components/Recommendations/DoctorsRow.js
import React, { useState, useRef, useEffect } from 'react';
import DoctorPopup from '../Popup/DoctorPopup';
import ReactDOM from 'react-dom';
import ReviewBlock from '../ReviewBlock';
import recUtils from '../../utils/RecUtils';
import websiteUtils from '../../utils/WebsiteUtils';

const DoctorsRow = ({ 
  doctors = [],
  hasMoreDoctors = false,
  isFallbackMode = false,
  isMobile = true // Default to mobile for backward compatibility
}) => {
  // Ensure doctors is always an array, even if passed as null
  const doctorsArray = doctors || [];

  // State to track if all doctors are being shown (mobile)
  const [showAllDoctors, setShowAllDoctors] = useState(false);
  // State to track if all doctors are being shown (desktop)
  const [showAllDoctorsDesktop, setShowAllDoctorsDesktop] = useState(false);
  const [selectedDoctor, setSelectedDoctor] = useState(null);
  const [isPopupOpen, setIsPopupOpen] = useState(false);
  const [doctorQuotesData, setDoctorQuotesData] = useState({});
  const [loading, setLoading] = useState({});
  
  const contentRef = useRef(null);
  const fullContentRef = useRef(null);
  const [maxHeight, setMaxHeight] = useState('0px');
  
  // Helper to parse quote strings in format "[reviewId] quote text"
  const parseQuoteString = (quoteStr) => {
    if (!quoteStr || typeof quoteStr !== 'string') return null;
    
    const match = quoteStr.match(/^\[(.*?)\]\s+(.+)$/);
    if (!match || !match[1] || !match[1].trim()) return null;
    
    return {
      reviewId: match[1].trim(),
      quoteText: match[2]
    };
  };

  // Helper to format timestamp
  const formatTimestamp = (timestampValue) => {
    if (!timestampValue) return "";
    
    let date;
    if (timestampValue && typeof timestampValue.toDate === 'function') {
      date = timestampValue.toDate();
    } else {
      date = new Date(timestampValue);
    }
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };
  
  // Measure content and update maxHeight when expanding/collapsing (mobile only)
  useEffect(() => {
    if (!contentRef.current || !fullContentRef.current) return;
    
    if (showAllDoctors) {
      // When expanded, set maxHeight to the full content height
      const fullHeight = fullContentRef.current.scrollHeight;
      setMaxHeight(`${fullHeight}px`);
    } else {
      // When collapsed, set maxHeight to show only the first row
      const rowRef = contentRef.current.querySelector('.doctor-row');
      if (rowRef) {
        const rowHeight = rowRef.scrollHeight;
        setMaxHeight(`${rowHeight}px`);
      }
    }
  }, [showAllDoctors, doctorsArray.length]); // Using doctorsArray instead of doctors to avoid null issues
  
  // Load quotes data for desktop view
  useEffect(() => {
    if (isMobile) return; // Only fetch data for desktop view
    
    // Create async function to fetch quotes for a doctor
    const fetchQuotesForDoctor = async (doctor) => {
      if (!doctor || loading[doctor.doctorName]) return;
      
      setLoading(prev => ({ ...prev, [doctor.doctorName]: true }));
      
      try {
        const doctorReviewsPros = Array.isArray(doctor.doctorReviewsPros) ? doctor.doctorReviewsPros : [];
        const doctorReviewsCons = Array.isArray(doctor.doctorReviewsCons) ? doctor.doctorReviewsCons : [];
        
        if (doctorReviewsPros.length === 0 && doctorReviewsCons.length === 0) {
          setLoading(prev => ({ ...prev, [doctor.doctorName]: false }));
          return;
        }
        
        const allQuotes = [];
        
        for (const quoteStr of doctorReviewsPros) {
          const parsed = parseQuoteString(quoteStr);
          if (parsed && parsed.reviewId) {
            allQuotes.push({ ...parsed, isPro: true });
          }
        }
        
        for (const quoteStr of doctorReviewsCons) {
          const parsed = parseQuoteString(quoteStr);
          if (parsed && parsed.reviewId) {
            allQuotes.push({ ...parsed, isPro: false });
          }
        }
        
        if (allQuotes.length === 0) {
          setLoading(prev => ({ ...prev, [doctor.doctorName]: false }));
          return;
        }
        
        const reviewIds = [...new Set(allQuotes.map(q => q.reviewId))];
        
        if (reviewIds.length > 20) {
          reviewIds.length = 20;
        }
        
        const reviewsData = {};
        
        for (const reviewId of reviewIds) {
          try {
            const reviewData = await recUtils.fetchReviewFromId(reviewId);
            
            if (!reviewData) {
              continue;
            }
            
            const prosQuotes = allQuotes
              .filter(q => q.reviewId === reviewId && q.isPro)
              .map(q => q.quoteText);
            
            const consQuotes = allQuotes
              .filter(q => q.reviewId === reviewId && !q.isPro)
              .map(q => q.quoteText);
            
            // Use centralized method for website source info
            const websiteInfo = websiteUtils.getWebsiteSourceInfo(reviewData);
            const reviewerName = reviewData.reviewer_name || "Anonymous";
            
            // Combine quotes with sentiment for ReviewBlock
            const displayedQuotes = [
              ...prosQuotes.map(text => ({ text, sentiment: 'positive' })),
              ...consQuotes.map(text => ({ text, sentiment: 'negative' }))
            ];
            
            reviewsData[reviewId] = {
              reviewId,
              reviewerName,
              satisfactionLevel: reviewData.user_satisfaction || -1,
              externalUrl: websiteInfo.external_url,
              websiteSource: websiteInfo.displayName,
              siteLogo: websiteInfo.siteLogo,
              authorImg: null, // Will use dynamic profile picture assignment
              operationDate: reviewData.timestamp ? formatTimestamp(reviewData.timestamp) : "",
              dynamicLabels: [], // No dynamic labels for doctor reviews
              displayedQuotes,
              isExternal: websiteInfo.isExternal
            };
          } catch (err) {
            console.error(`Error fetching review ${reviewId}:`, err);
          }
        }
        
        setDoctorQuotesData(prev => ({
          ...prev,
          [doctor.doctorName]: reviewsData
        }));
      } catch (err) {
        console.error("Error fetching quotes for doctor:", doctor.doctorName, err);
      } finally {
        setLoading(prev => ({ ...prev, [doctor.doctorName]: false }));
      }
    };
    
    // Fetch quotes for all doctors - using doctorsArray to avoid null errors
    if (doctorsArray.length > 0) {
      doctorsArray.forEach(doctor => {
        fetchQuotesForDoctor(doctor);
      });
    }
  }, [isMobile, doctorsArray]); // Using doctorsArray instead of doctors
  
  // Cleanup profile picture assignments when component unmounts
  useEffect(() => {
    return () => {
      // Collect all review IDs to release
      for (const doctorName in doctorQuotesData) {
        const reviews = doctorQuotesData[doctorName];
        for (const reviewId in reviews) {
          recUtils.releaseProfilePicture(reviewId);
        }
      }
    };
  }, [doctorQuotesData]);
  
  // If no doctors, don't render this component
  if (doctorsArray.length === 0) {
    return null;
  }
  
  // Handle toggle click (mobile)
  const handleToggleClick = () => {
    setShowAllDoctors(prev => !prev);
  };

  // Handle desktop toggle click
  const handleDesktopToggleClick = () => {
    setShowAllDoctorsDesktop(prev => !prev);
  };

  // Handle doctor card click
  const handleDoctorClick = (doctor) => {
    setSelectedDoctor(doctor);
    setIsPopupOpen(true);
  };
  
  // Handle popup close
  const handlePopupClose = () => {
    setIsPopupOpen(false);
  };
  
  // Create a portal for the popup to avoid any containment issues
  const renderPopup = () => {
    // Only render the popup if it's open
    if (!isPopupOpen || !selectedDoctor) return null;
    
    // Check if the DOM is available (we're in the browser)
    if (typeof document !== 'undefined') {
      return ReactDOM.createPortal(
        <DoctorPopup 
          isOpen={isPopupOpen}
          onClose={handlePopupClose}
          doctor={selectedDoctor}
          isFallbackMode={isFallbackMode}
        />,
        document.body
      );
    }
    
    return null;
  };
  
  // Group doctors into rows of 2 for mobile - using doctorsArray to avoid null issues
  const doctorRows = [];
  for (let i = 0; i < doctorsArray.length; i += 2) {
    doctorRows.push(doctorsArray.slice(i, i + 2));
  }
  
  // MOBILE VIEW - Same as original implementation
  if (isMobile) {
    return (
      <div className="px-5 mt-2 pb-2">
<div className="flex flex-col md:flex-row md:items-center md:gap-3 mb-2">
  <h4 className="text-2xl font-bold text-gray-700">
    Featured Doctors
  </h4>

  {/* Only show toggle button if there are more than 2 doctors */}
  {doctorsArray.length > 2 && (
    <button 
      onClick={handleToggleClick}
      className="mt-1 md:mt-0 text-sm text-gray-400 font-bold underline transition-colors duration-200 flex items-center"
    >
      <span>{showAllDoctors ? 'Show Less' : `See All (${doctorsArray.length})`}</span>

      {/* Chevron icon that rotates */}
      <svg 
        className={`w-4 h-4 ml-1 transition-transform duration-300 ${showAllDoctors ? 'transform rotate-180' : ''}`} 
        fill="none" 
        stroke="currentColor" 
        viewBox="0 0 24 24"
      >
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
      </svg>
    </button>
  )}
</div>

        
        {/* Doctors content with overflow hidden and max-height transition */}
        <div 
          ref={contentRef}
          className="overflow-hidden transition-all duration-300 ease-in-out"
          style={{ maxHeight: maxHeight }}
        >
          <div ref={fullContentRef}>
            {doctorRows.map((row, rowIndex) => (
              <div 
                key={`row-${rowIndex}`}
                className={`doctor-row grid grid-cols-1 gap-1 ${rowIndex > 0 ? 'mt-2' : ''}`}
              >
                {row.map((doctor, colIndex) => {
                  const reviewCount = recUtils.getUniqueReviewCountOfDoctor(doctor);
                  const index = rowIndex * 2 + colIndex;
                  
                  return (
                    <div 
                      key={`doctor-${doctor.doctorName}-${index}`} 
                      className="flex items-center bg-transparent rounded-lg py-2 mb-2 md:m-2 opacity-100 cursor-pointer hover:bg-gray-100 active:bg-gray-200"
                      onClick={() => handleDoctorClick(doctor)}
                    >
                      {/* Container for doctor image and notification badge */}
                      <div className="relative flex-shrink-0">
                        {/* Image container with overflow hidden */}
                        <div className="w-28 h-28 bg-gray-300 rounded-lg overflow-hidden">
                          {doctor.doctorImageUrl ? (
                            <img 
                              src={doctor.doctorImageUrl} 
                              alt={doctor.doctorName}
                              className="w-full h-full object-cover"
                            />
                          ) : (
                            <img 
                              src={`https://placehold.co/56x56`} 
                              alt={doctor.doctorName}
                              className="w-full h-full object-cover"
                            />
                          )}
                        </div>
                        
                        {/* Review count bubble */}
                        {reviewCount > 0 && (
                          <div className="absolute -top-2 -right-2 bg-green-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold shadow-sm z-10">
                            {reviewCount}
                          </div>
                        )}
                      </div>
                      <div className=" ml-2 flex-1">
                        <h5 className="font-bold text-gray-800 text-lg">{doctor.doctorName}</h5>
                        <p className="text-gray-600 text-base">{doctor.doctorTitle}</p>
                      </div>
                    </div>
                  );
                })}
                
                {/* If we have an odd number of doctors in the last row, add an empty placeholder */}
                {row.length === 1 && rowIndex === doctorRows.length - 1 && (
                  <div className="invisible"></div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Render the popup using a portal */}
        {renderPopup()}
      </div>
    );
  }
  
  // DESKTOP VIEW - Updated to show only one doctor by default
  return (
    <div className="mt-4 mb-8">
<div className="mb-4">
<div className="my-6 border-t-2 border-gray-200 border-opacity-100" />

  <h4 className="font-bold text-gray-700 text-2xl mb-1">Featured Doctors</h4>

  {/* See All Doctors button - only show if there are more than 1 doctors */}
  {doctorsArray.length > 1 && (
    <button
      onClick={handleDesktopToggleClick}
      className="mt-1 text-gray-400 underline font-bold hover:text-gray-600 text-sm transition-colors duration-200"
    >
      {showAllDoctorsDesktop ? 'Show Less' : `See All Doctors (${doctorsArray.length})`}
    </button>
  )}
</div>

      
      {/* Doctors displayed - show only first doctor by default, or all if expanded */}
      <div className="space-y-8">
        {(showAllDoctorsDesktop ? doctorsArray : doctorsArray.slice(0, 1)).map((doctor, index) => {
          const { 
            doctorName, 
            doctorTitle, 
            doctorImageUrl, 
            doctorCredentials = [],
            doctorReviewsPros = [],
            doctorReviewsCons = []
          } = doctor;
          
          const hasBulletPoints = Array.isArray(doctorCredentials) && doctorCredentials.length > 0;
          const hasReviews = 
            (Array.isArray(doctorReviewsPros) && doctorReviewsPros.length > 0) || 
            (Array.isArray(doctorReviewsCons) && doctorReviewsCons.length > 0);
          
          const quotesData = doctorQuotesData[doctorName] || {};
          const isLoadingQuotes = loading[doctorName];
          
          return (
            <div key={`doctor-expanded-${index}`} className="bg-transparent rounded-lg border-2 border-gray-200 overflow-hidden ">
              {/* Doctor header */}
              <div className="py-4 px-6 bg-transparent">
                <div className="flex items-start">
                  {/* Doctor image */}
                  <div className="w-32 h-32 bg-gray-200 rounded-lg mr-5 flex-shrink-0 overflow-hidden">
                    {doctorImageUrl ? (
                      <img 
                        src={doctorImageUrl} 
                        alt={doctorName}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full bg-gray-300 flex items-center justify-center text-gray-500">
                        <svg className="w-12 h-12" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z" clipRule="evenodd" />
                        </svg>
                      </div>
                    )}
                  </div>
                  
                  {/* Doctor info */}
                  <div className="flex-1">
                    <h3 className="font-bold text-gray-800 text-lg">
                      {doctorName || 'Unknown Doctor'}
                    </h3>
                    
                    <p className="text-gray-600 text-md mt-1">
                      {doctorTitle || 'Cosmetic Surgeon, M.D.'}
                    </p>
                    
{/* Bullet points - only show if credentials exist */}
{hasBulletPoints && (
  <ul className="mt-4 flex flex-wrap gap-2">
    {doctorCredentials.map((credential, idx) => (
      <li 
        key={idx} 
        className="flex items-center bg-blue-100 w-fit p-1 rounded-2xl px-3 shrink-0"
      >
        <span className="text-gray-600 mr-2">
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path 
              fillRule="evenodd" 
              d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" 
              clipRule="evenodd" 
            />
          </svg>
        </span>
        <span className="text-sm text-gray-700">{credential}</span>
      </li>
    ))}
  </ul>
)}

                  </div>
                </div>
              </div>
              
              {/* Doctor quotes section - only show if we have reviews */}
              {hasReviews && (
                <div className="px-2 pb-6">
                  {isLoadingQuotes ? (
                    <div className="flex justify-center py-4">
                      <div className="animate-spin rounded-full h-6 w-6 border-t-2 border-b-2 border-mm_blue" />
                    </div>
                  ) : Object.keys(quotesData).length === 0 ? (
                    <div className="text-gray-500 text-sm py-2">
                      No review data available
                    </div>
                  ) : (
                    <div className="space-y-6">
                      {Object.values(quotesData).map((reviewBlockData, idx) => (
                        <ReviewBlock
                          key={idx}
                          {...reviewBlockData}
                          className=" border-gray-200"
                          virtualizeRendering={false}
                        />
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default DoctorsRow;